/**
 * A real credentialing/licensure model (`BACKLOG.md` C6b), resolving the gap `noteAttestation.ts`'s
 * own header has documented since C6 landed: the note co-sign gate was role-based only
 * (`note:cosign`, held by `physician`) with no record of an actual license, its status, or its
 * expiry — "does this person hold the physician role" standing in for "does this person hold an
 * active, unexpired, correct-type license", which is a materially weaker claim.
 *
 * Schema matches `emr-po`'s own §10a design (`~/Downloads/emr-po/formbuilder-spec-2026-08-20.md`,
 * shared cross-repo so a future form-engine credential check reads the same table) — but this pass
 * wires up only what this codebase's note co-sign gate actually needs today
 * (`hasActiveCosignCredential` below). That spec's fuller design — a per-requirement
 * `required_credential_type`, the shared `signature_requirements` ledger it's part of — has no
 * second consumer in this codebase yet (no form-submission surface exists), so it is deliberately
 * not built now, per this workspace's own "favor the general, reusable solution once a pattern
 * actually recurs" rule: the recurrence hasn't happened yet. `C6a`'s double-cosign fix
 * (`notesService.ts`'s `cosignNote`) instead uses a database-level compare-and-swap, not this ledger
 * — see that function's own comment.
 */

import { z } from 'zod';

export const CREDENTIAL_TYPES = ['MD', 'DO', 'NP', 'PA', 'RN'] as const;
export type CredentialType = (typeof CREDENTIAL_TYPES)[number];

export const CREDENTIAL_STATUSES = ['active', 'expired', 'suspended', 'revoked'] as const;
export type CredentialStatus = (typeof CREDENTIAL_STATUSES)[number];

/** The credential types that satisfy "licensed supervising provider" for note co-signing — matches
 * this app's existing `note:cosign` role gate (`physician` only), whose realistic license types are
 * MD/DO. Not every `CREDENTIAL_TYPES` value qualifies; NP/PA/RN are tracked here because a real
 * credentialing model covers every clinical role, not because they currently unlock anything. */
export const COSIGN_ELIGIBLE_CREDENTIAL_TYPES: readonly CredentialType[] = ['MD', 'DO'];

/** The minimal shape the eligibility check needs — never the full row, matching
 * `noteAttestation.ts`'s own `NoteAttestationSubject` reasoning: a caller should not be able to hand
 * this module more than the decision requires. */
export interface CredentialEligibilityRecord {
  readonly credentialType: string;
  readonly status: string;
  /** Calendar-date string (`YYYY-MM-DD`) or null — matches `db/schema/signatures.ts`'s
   * `userCredentials.expiresAt`. Null means "does not expire". */
  readonly expiresAt: string | null;
}

/**
 * Does this set of credentials include at least one active, unexpired, cosign-eligible (MD/DO)
 * credential? Pure — takes `today` as a parameter rather than reading the clock itself, so it stays
 * unit-testable with no wall-clock dependency, matching this codebase's own no-`Date.now()`-in-pure-
 * modules convention.
 *
 * **Self-reported, not gated on verification** — `emr-po`'s own decision, 2026-08-20: an entered
 * credential satisfies this check immediately, whether or not `verifiedAt` is set. `verifiedAt`
 * exists for a future admin-facing report, not as a gate here.
 */
export function hasActiveCosignCredential(
  credentials: readonly CredentialEligibilityRecord[],
  today: string,
): boolean {
  return credentials.some(
    (c) =>
      (COSIGN_ELIGIBLE_CREDENTIAL_TYPES as readonly string[]).includes(c.credentialType) &&
      c.status === 'active' &&
      (c.expiresAt === null || c.expiresAt >= today),
  );
}

// --- Request/response schemas --------------------------------------------------------------------

export const CREDENTIAL_ERROR_CODES = [
  /** The body failed schema validation. */
  'invalid_request',
  /** No such credential in the caller's own tenant, or it does not belong to the caller. */
  'not_found',
] as const;
export type CredentialErrorCode = (typeof CREDENTIAL_ERROR_CODES)[number];

const credentialTypeSchema = z.enum(CREDENTIAL_TYPES);
const credentialStatusSchema = z.enum(CREDENTIAL_STATUSES);

export const MAX_LICENSE_NUMBER_LENGTH = 100;
export const MAX_LICENSE_STATE_LENGTH = 50;

export const createCredentialRequestSchema = z
  .object({
    credentialType: credentialTypeSchema,
    licenseNumber: z.string().trim().min(1).max(MAX_LICENSE_NUMBER_LENGTH).nullable().default(null),
    licenseState: z.string().trim().min(1).max(MAX_LICENSE_STATE_LENGTH).nullable().default(null),
    status: credentialStatusSchema.default('active'),
    issuedAt: z.iso.date().nullable().default(null),
    expiresAt: z.iso.date().nullable().default(null),
  })
  .strict();
export type CreateCredentialRequest = z.infer<typeof createCredentialRequestSchema>;

export const credentialResponseSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  credentialType: z.string(),
  licenseNumber: z.string().nullable(),
  licenseState: z.string().nullable(),
  status: z.string(),
  issuedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type CredentialResponse = z.infer<typeof credentialResponseSchema>;

export const credentialEnvelopeSchema = z.object({ credential: credentialResponseSchema });
export const credentialListEnvelopeSchema = z.object({
  credentials: z.array(credentialResponseSchema),
});
